import { computed, type IComputedValue, observable, reaction, runInAction } from "mobx"
import { toast } from "sonner"

import { DropZoneModel } from "@/components/DropZone/DropZoneModel.ts"
import { withGlobalLoader } from "@/lib/context.ts"
import { getLogger } from "@/lib/logging.ts"
import { Model } from "@/lib/Model.ts"
import { projectQueryKey, projectsV2QueryKey } from "@/lib/queries.ts"

import type { ProjectColorSwatch } from "@/components/ProjectCoverEditor/ProjectColorPalette.ts"
import type { PR_Project2, ProjectColorKey, ProjectKey, TID } from "@/lib/client/generated/TypeDefinitions.ts"
import type { OrgContext } from "@/lib/context.ts"
import type { IObservableValue } from "mobx"

const log = getLogger("ProjectCoverModel")


export class ProjectCoverModel extends Model {
  private readonly project = observable.box<PR_Project2 | null>(null)

  readonly dropZoneModel: DropZoneModel

  readonly isReady = observable.box(false)
  readonly isUpdating = observable.box(false)
  readonly isUploading = observable.box(false)

  readonly cover: IComputedValue<string | null>
  readonly color: IObservableValue<ProjectColorSwatch | null>

  readonly showCover = computed(() => {
    return this.isReady.get() && (Boolean(this.cover.get()) || this.isLoading.get())
  })

  readonly hasError = computed(() => {
    return this.dropZoneModel.hasError.get()
  })

  constructor(
    private readonly ctx: OrgContext,
    private readonly projectKey: ProjectKey,
    projectColorKey: ProjectColorKey | null
  ) {
    super("ProjectCoverModel")

    this.dropZoneModel = new DropZoneModel({
      accept: ["image/png", "image/jpeg", "image/gif"],
      onFileSelected: this.onFileSelected,
    })

    this.lifetime.add(() => this.dropZoneModel.terminate(), "ProjectCoverModel:dropZoneModel")

    this.color = observable.box(projectColorKey)
    this.cover = computed(() => {
      const projectCover = this.project.get()?.cover
      if (!projectCover) {
        return null
      }

      return this.ctx.queries.apiClient.buildStaticUrl(`/attachments/${projectCover}`)
    })

    this.lifetime.add(reaction(
      () => ({
        isUpdating: this.isUpdating.get(),
        isUploading: this.isUploading.get(),
      }),
      ({ isUpdating, isUploading }) => {
        this.isLoading.set(isUpdating || isUploading)
      }
    ), "ProjectCoverModel:isLoading")

    this.refresh(false).catch((e) => log.error("Failed to refresh", e))
  }

  readonly selectColor = async (color: ProjectColorSwatch) => {
    runInAction(() => {
      this.color.set(color)
    })

    try {
      await withGlobalLoader(this.ctx, this.updateColor(color))
    } catch (error) {
      toast.error((error as Error).message)
    }
  }

  readonly removeCover = async () => {
    runInAction(() => {
      this.isUpdating.set(true)
    })

    try {
      await withGlobalLoader(this.ctx, this.updateCover(null))
    } catch (error) {
      toast.error((error as Error).message)
    }

    runInAction(() => {
      this.isUpdating.set(false)
    })
  }

  private async refresh(force = true) {
    runInAction(() => {
      this.isUpdating.set(false)
    })

    if (force) {
      void this.ctx.queryClient.invalidateQueries(projectQueryKey(this.projectKey))
    }

    const project = await this.ctx.queries.getProjectV2(this.projectKey)
    runInAction(() => {
      this.project.set(project)
      this.isReady.set(true)
      this.isUpdating.set(false)
    })
  }

  private readonly onFileSelected = async (file: File) => {
    runInAction(() => {
      this.isUploading.set(true)
    })

    try {
      const attachment = await withGlobalLoader(this.ctx, this.ctx.queries.apiClient.uploadFile(file))
      await withGlobalLoader(this.ctx, this.updateCover(attachment.id))
    } catch (error) {
      toast.error((error as Error).message)
    }

    runInAction(() => {
      this.isUploading.set(false)
    })
  }

  private async updateCover(attachmentId: TID | null) {
    await withGlobalLoader(this.ctx, this.updateProjectCover(this.projectKey, attachmentId))
    await withGlobalLoader(this.ctx, this.refresh())
  }

  private async updateProjectCover(projectKey: ProjectKey, attachmentId: TID | null) {
    await this.ctx.queries.apiClient.updateProjectCover(projectKey, attachmentId)
    void this.ctx.queryClient.invalidateQueries({ queryKey: projectsV2QueryKey })
    void this.ctx.queryClient.invalidateQueries(projectQueryKey(projectKey))
  }

  private async updateColor(swatchID: ProjectColorSwatch) {
    await withGlobalLoader(this.ctx, this.updateProjectColor(this.projectKey, swatchID))
    await withGlobalLoader(this.ctx, this.refresh())
  }

  private async updateProjectColor(projectKey: ProjectKey, swatchID: ProjectColorSwatch) {
    await this.ctx.queries.apiClient.updateProjectColor(projectKey, swatchID)
    void this.ctx.queryClient.invalidateQueries({ queryKey: projectsV2QueryKey })
    void this.ctx.queryClient.invalidateQueries(projectQueryKey(projectKey))
  }
}
